import Input from "@/app/components/ui/Inputs/Input";
import GeoSearchSelectMenu from "@/app/components/ui/SelectMenus/GeoSearchSelectMenu";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useOrgCitiesListAPI } from "@/app/hooks/api_hooks/org/useOrgCitiesListAPI";
import { useArrayConstants } from "@/app/hooks/useArrayConstants";
import useFormValidations from "@/app/hooks/useFormValidations";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { IRotaState } from "@/app_redux/reducers/slices/auth/rota_slice";
import clsx from "clsx";
import { useEffect } from "react";
import { IAddressBlockProps } from "@components/routes/auth_routes/CustomerProfile/customer_profile_interface";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";

const AddressBlock: React.FC<IAddressBlockProps> = ({
  formHook,
  isFormUpdate,
  disabled,
  addressFieldActions,
  label,
}) => {
  const { getRouteKey } = useRouter();
  const routeId = getRouteKey("ROTA", "id");
  const { routeState } = useRouteInfo(routeId);

  const { cities: cityList } = routeState.state as IRotaState;

  const { forOnlyNumber, forOnlyAlphabet, forLatitude, forLongitude } = useFormValidations();

  const { indianStates, address_labels: addressLabels } = useArrayConstants();

  const fetchCities = useOrgCitiesListAPI("ROTA");

  const cityOrgItem = useSelectMenuReducer(cityList, "name", "id");

  const isBlockDisabled = disabled && disabled === true;
  // RBAC: CUSTOMER_ADDRESS_BLUECITY_VARIANT — legacy role: clinic_ops_bluecity
  const isBluecity = useFeatureAccess("CUSTOMER_ADDRESS_BLUECITY_VARIANT");


  useEffect(() => {
    fetchCities({
      withClinics: false,
      staffCategoryId: "2",
      withClinicProviders: false,
      clinicOptions: {
        selectFirst: true,
      },
    });
  }, []);

  const renderCheck = (addressField: any): boolean => {
    if (isFormUpdate === true && label) {
      return isFormUpdate && label && addressField?.label === label;
    } else {
      return true;
    }
  };

  return addressFieldActions?.fields.map((addressField, index: number) => {
    return (
      renderCheck(addressField) && (
        <div className="w-full flex flex-col gap-3 pb-6 rounded-md">
          <div className="w-full">
            <Input
              key={addressField?.id}
              disabled={isBlockDisabled}
              placeholder="Enter H NO., Street Name, Landmark"
              className="text-xs w-full"
              fieldError={
                formHook?.formState?.errors?.addresses ? formHook?.formState?.errors?.addresses[index]?.address_1 : null
              }
              register={formHook.register(`addresses.${index}.address_1`, {
                required: false,
              })}
              errorMessages={[{ type: "required", message: "Address 1 is required" }]}
            />
          </div>
          <div className="w-full">
            <GeoSearchSelectMenu
              autoComplete="off"
              disabled={isBlockDisabled}
              disableSearch={isBlockDisabled}
              autoCorrect="false"
              showDropdownWhenNoData
              withClearAction
              defaultAddressString={isFormUpdate ? addressField.address_2 : ""}
              onClear={() => {
                formHook.setValue(`addresses.${index}.address_2`, null);
                formHook.setValue(`addresses.${index}.city`, null);
                formHook.setValue(`addresses.${index}.pincode`, null);
                formHook.setValue(`addresses.${index}.state`, null);
                formHook.setValue(`addresses.${index}.latitude`, null);
                formHook.setValue(`addresses.${index}.longitude`, null);
              }}
              onSelectItem={(item) => {
                if (item) {
                  formHook.setValue(`addresses.${index}.address_2`, item.state.formattedAddress);
                  formHook.setValue(`addresses.${index}.city`, item.state.city !== "" ? item.state.city : null);
                  formHook.setValue(
                    `addresses.${index}.latitude`,
                    item.state.lat !== "" ? item.state.lat.toString() : null,
                  );
                  formHook.setValue(
                    `addresses.${index}.longitude`,
                    item.state.long !== "" ? item.state.long.toString() : null,
                  );

                  formHook.setValue(
                    `addresses.${index}.pincode`,
                    item.state.pincode === "" ? null : item.state.pincode,
                  );
                  formHook.setValue(`addresses.${index}.state`, item.state.state !== "" ? item.state.state : null);
                }
                formHook.clearErrors(`addresses.${index}.address_2`);
              }}
              errorMessages={[{ message: "", type: "required" }]}
              fieldError={
                formHook?.formState?.errors?.addresses ? formHook?.formState?.errors?.addresses[index]?.address_2 : null
              }
              register={formHook.register(`addresses.${index}.address_2`, {
                required: !isBluecity,
              })}
              showTypedErrors={true}
              placeholder="Search Google Location"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full">
            <div>
              <Input
                className="w-full text-xs"
                placeholder="Latitude"
                fieldError={formHook.formState.errors?.addresses?.[index]?.latitude}
                disabled={isBlockDisabled}
                register={formHook.register(`addresses.${index}.latitude`, {
                  ...forLatitude.validations,
                  required: !isBluecity,
                })}
                errorMessages={[forLatitude.errors, { type: "required", message: "" }]}
              />
            </div>
            <div>
              <Input
                className="w-full text-xs"
                placeholder="Longitude"
                fieldError={formHook.formState.errors?.addresses?.[index]?.longitude}
                disabled={isBlockDisabled}
                register={formHook.register(`addresses.${index}.longitude`, {
                  ...forLongitude.validations,
                  required: !isBluecity,
                })}
                errorMessages={[forLongitude.errors, { type: "required", message: "" }]}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 w-full items-start">
            <div>
              <Input
                className="w-full text-xs"
                placeholder="Pincode"
                maxLength={6}
                fieldError={formHook.formState.errors?.addresses?.[index]?.pincode}
                disabled={isBlockDisabled}
                register={formHook.register(`addresses.${index}.pincode`, {
                  ...forOnlyNumber.validations,
                  required: !isBluecity,
                  maxLength: 6,
                })}
                errorMessages={[
                  forOnlyNumber.errors,
                  { type: "required", message: "" },
                  { type: "maxLength", message: "Too long" },
                ]}
              />
            </div>
            <div>
              <SearchableSelectMenu
                onSelectItem={(item) => {
                  if (item) {
                    formHook.setValue(`addresses.${index}.city`, item.title);
                  }
                }}
                fieldError={formHook.formState.errors?.addresses?.[index]?.city}
                register={formHook.register(`addresses.${index}.city`, {
                  ...forOnlyAlphabet.validations,
                  required: !isBluecity,
                })}
                disabled={isBlockDisabled}
                errorMessages={[forOnlyAlphabet.errors, { type: "required", message: "" }]}
                selectItems={cityOrgItem}
                placeholder="Select City"
                showTooltips={true}
                showTypedErrors={true}
                showDropdownIcon={true}
                defaultSelected={cityOrgItem?.filter((oc) => oc.title === formHook.watch(`addresses.${index}.city`))[0]}
                listBoxClassName="w-full"
                className="text-gray-800 "
                containerClassName="w-full"
              />
            </div>
            <div className="w-full">
              <SearchableSelectMenu
                onSelectItem={(item) => {
                  if (item) {
                    formHook.setValue(`addresses.${index}.state`, item.title);
                  }
                }}
                fieldError={formHook.formState.errors?.addresses?.[index]?.state}
                register={formHook.register(`addresses.${index}.state`, {
                  ...forOnlyAlphabet.validations,
                  required: !isBluecity,
                })}
                disabled={isBlockDisabled}
                errorMessages={[forOnlyAlphabet.errors, { type: "required", message: "" }]}
                selectItems={indianStates}
                placeholder="Select State"
                showTooltips={false}
                showTypedErrors={true}
                showDropdownIcon={true}
                defaultSelected={
                  indianStates?.filter((oc) => oc.title === formHook.watch(`addresses.${index}.state`))[0]
                }
                listBoxClassName="w-full"
                className="text-gray-800 "
                containerClassName="w-full"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 w-full pt-1">
            {addressLabels.length > 0 &&
              addressLabels.map((adL, idx) => {
                return (
                  <div key={`${adL.label}:${idx}`} className="w-full">
                    <button
                      disabled={isBlockDisabled}
                      onClick={(e) => {
                        e.preventDefault();
                        formHook.setValue(`addresses.${index}.label`, adL?.label);
                      }}
                      className={clsx(
                        "border opacity-100 w-full py-2 rounded-md text-xs",
                        formHook.watch(`addresses.${index}.label`) === adL.label
                          ? "border-[#193a6a] opacity-100 border focus:border-gray-400 hover:bg-[rgba(128,141,160,0.1)] hover:border-gray-200 bg-gray-100"
                          : "text-gray-400 border-[rgba(128, 141, 160, 0.20)] focus:border-[#193a6a] hover:bg-[rgba(128,141,160,0.1)] hover:border-gray-200",
                      )}
                      id="address_home"
                    >
                      {adL.value}
                    </button>
                  </div>
                );
              })}
            <div className="-mt-3 text-xs">
              <Input
                disabled={isBlockDisabled}
                type="text"
                register={formHook.register(`addresses.${index}.label` as const, { required: false })}
                fieldError={
                  formHook?.formState?.errors?.addresses ? formHook?.formState?.errors?.addresses[index]?.label : null
                }
                // errorMessages={[{ type: "required", message: "Address type is required." }]}
                errorMessages={[]}
                hidden
              />
            </div>
          </div>
        </div>
      )
    );
  });
};

export default AddressBlock;
